/* eslint-disable camelcase */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { debounce } from 'lodash';

import { useRetracked } from 'js/lib/retracked';
import { randomUUID } from 'js/lib/uuid';

import { TextField } from '@coursera/cds-core';
import { SendIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachIconButton } from 'bundles/ai-coach-platform/components/building-blocks';
import SpeechToTextButton from 'bundles/ai-coach-platform/components/patterns/chat/common/SpeechToTextButton';
import useSpeechToText, {
  SpeechRecognitionStatus,
  type SpeechToTextError,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/VoiceInput/useSpeechToText';
import SuggestedActionsPopover from 'bundles/ai-coach-platform/components/patterns/popover/SuggestedActionsPopover';
import type { Action as SuggestedAction } from 'bundles/ai-coach-platform/components/patterns/popover/types';
import {
  getInputMethod,
  mapSpeechRecognitionErrorToFailureType,
} from 'bundles/ai-coach-platform/utils/dictationTracking';
import type {
  DictationAttemptTrackingData,
  DictationTrackingContext,
  SendCoachMessageTrackingMetadata,
} from 'bundles/ai-coach-platform/utils/dictationTracking';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';
import { useFocusOnceWhenReady } from 'bundles/ai-coach-platform/utils/useFocusOnceWhenReady';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  root: ({ isFocused = false, disabled = false }) => css`
    display: flex;
    flex-direction: column;
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: var(--cds-border-radius-100);
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
    outline: ${isFocused ? '1px solid var(--cds-color-emphasis-neutral-background-xstrong) !important' : 'none'};

    &:hover {
      outline: 1px solid var(--cds-color-blue-700);
    }

    ${
      disabled &&
      `
      opacity: 0.8;
      pointer-events: none;

      .cds-input-input::placeholder {
        opacity: 0.4;
      }
    }
    `
    }
  `,

  preview: css`
    display: flex;
    overflow: auto;
    margin: var(--cds-spacing-50) 0;
  `,

  textField: (isListening: boolean) => css`
    /* Coach branding overrides on CDS Textfield */
    .cds-input-multiline {
      margin-bottom: var(--cds-spacing-100);
      background: transparent;

      fieldset {
        border: none !important;
      }
    }

    textarea.coach-rich-input-field-input {
      padding: var(--cds-spacing-50) 0;
      caret-color: ${
        isListening
          ? 'var(--cds-color-emphasis-primary-stroke-default)'
          : 'var(--cds-color-interactive-secondary-selected)'
      };
    }

    .cds-input-input {
      background: var(--cds-color-neutral-background-primary-weak);
      color: var(--cds-color-interactive-secondary-selected);

      &:focus {
        color: var(--cds-color-interactive-secondary-selected);
      }

      &:hover::placeholder {
        color: var(--cds-color-neutral-primary-weak) !important;
      }
    }

    .cds-input-focused {
      &::before {
        display: none;
      }

      &:hover {
        background: var(--cds-color-neutral-background-primary-weak) !important;
      }

      .cds-input-input::placeholder {
        color: var(--cds-color-neutral-primary-weak) !important;
      }
    }
  `,

  footer: css`
    display: flex;
    flex-wrap: wrap;
  `,

  actionButtons: css`
    display: flex;
    gap: var(--cds-spacing-50);
  `,

  voiceTextInput: (disabled = false) => css`
    display: flex;
    flex: 1;
    gap: var(--cds-spacing-50);
    justify-content: flex-end;

    button {
      cursor: ${disabled ? 'not-allowed !important' : 'pointer'};

      &:focus,
      &:hover {
        background-color: ${disabled ? 'none' : 'var(--cds-color-blue-50)'};
      }
    }
  `,
};

export type Props = {
  /**
   * custom placeholder for the input field
   */
  placeholder?: string;

  /**
   * Custom preview area for media attachments
   */
  preview?: React.ReactNode;
  /**
   * Custom action buttons to be displayed in the footer
   */
  actionButtons?: React.ReactNode;
  /**
   * Custom input field component to be used instead of the default TextField
   */
  input?: React.ReactNode;
  /**
   * Callback for when the text input changes
   */
  onTextInputChange?: (text: string) => void;
  /**
   * Callback for when the user sends a message
   */
  onSend?: (text: string, metadata?: SendCoachMessageTrackingMetadata) => void;

  /**
   * Custom aria-label for input field
   */
  'aria-label'?: string;

  /**
   * Whether the input field should be specifically disabled
   */
  disableInput?: boolean;

  /**
   * Whether the speech to text button should be disabled
   */
  disableSpeechToText?: boolean;

  /**
   * Tooltip text for when speech to text button is disabled
   */
  speechToTextDisabledTooltip?: string;

  /**
   * Whether the entire component is disabled
   */
  disabled?: boolean;

  /**
   * Whether to show/hide the speech-to-text button
   *
   * @default false
   */
  showSpeechToTextButton?: boolean;

  /**
   * Controlled text value for the input field
   */
  value?: string;

  /**
   * Controlled focus state
   */
  focused?: boolean;

  /**
   * Maximum number of characters allowed in the text input.
   */
  maxLength?: number;

  /**
   * Whether the input field should be autofocused on mount
   */
  autofocus?: boolean;

  /**
   * Suggested actions for "Ask" button to be displayed in the footer
   */
  suggestedActions?: SuggestedAction[];

  /**
   * When true, the send button is enabled and `onSend` fires even if the
   * input text is empty. Use this when the parent composes additional content
   * (e.g. selected suggestion chips) outside this component and wants to
   * commit selections even without typed text.
   */
  allowSendWithEmptyMessage?: boolean;

  /**
   * Optional eventing context for dictation attempts. If omitted, no dictation_attempt
   * events will be sent from this shared component.
   */
  dictationTrackingContext?: DictationTrackingContext;
};

export type RichInputFieldRef = {
  focus: () => void;
};

type ActiveDictationAttempt = {
  attemptId: string;
  hasFinalTranscript: boolean;
  hasSpeechDetected: boolean;
};

/**
 * Rich input field for Coach chat. Supports plain-text input as well as
 * voice input by default when supported by browsers.
 */
export const RichInputField = React.forwardRef<RichInputFieldRef, Props>((props, forwardRef): React.ReactElement => {
  const {
    onSend,
    onTextInputChange,
    disableInput = false,
    disableSpeechToText = false,
    speechToTextDisabledTooltip = '',
    disabled = false,
    showSpeechToTextButton = true,
    'aria-label': ariaLabel,
    placeholder = _t('Ask me anything...'),
    input,
    preview,
    actionButtons,
    suggestedActions,
    value = '',
    focused = false,
    maxLength,
    autofocus = false,
    dictationTrackingContext,
    allowSendWithEmptyMessage = false,
  } = props;
  const [inputText, setInputText] = React.useState<string>(value);
  const inputTextRef = React.useRef<string>(value);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const clearTranscriptRef = React.useRef<() => void>(() => {});
  const recognitionRef = React.useRef<SpeechRecognition | null>(null);
  const stopListeningRef = React.useRef<() => void>(() => {});
  const listeningRef = React.useRef<boolean>(false);
  const activeDictationAttemptRef = React.useRef<ActiveDictationAttempt | null>(null);
  const hasDictatedTextInDraftRef = React.useRef<boolean>(false);
  const [interimTranscript, setInterimTranscript] = React.useState<string>('');

  // whether the component as a whole should maintain focused state (primarily for a11y)
  const [isFocused, setIsFocused] = React.useState<boolean>(focused);
  // whether text was manually edited by user before sending
  const [editedInput, setEditedInput] = React.useState<boolean>(false);

  const truncateToMaxLength = React.useCallback<(text: string) => string>(
    (text: string) => {
      if (maxLength === undefined) {
        return text;
      }

      return text.slice(0, maxLength);
    },
    [maxLength]
  );

  const truncateToRemainingLength = React.useCallback<(text: string, baseText: string) => string>(
    (text: string, baseText: string) => {
      if (maxLength === undefined) {
        return text;
      }

      return text.slice(0, Math.max(maxLength - baseText.length, 0));
    },
    [maxLength]
  );

  React.useEffect(() => {
    const truncatedValue = truncateToMaxLength(value);
    setInputText(truncatedValue);
    inputTextRef.current = truncatedValue;
  }, [truncateToMaxLength, value]);

  React.useEffect(() => {
    setIsFocused(focused);
  }, [focused]);

  // Native `autoFocus` only fires on mount and is gated by `disableInput`, so a
  // composer that mounts disabled (e.g. while the first AI response is loading)
  // never receives focus. This picks up the first disabled-to-enabled
  // transition.
  useFocusOnceWhenReady(inputRef, autofocus && !disabled && !disableInput);

  React.useEffect(() => {
    return () => {
      // clean up STT resources on unmount
      clearTranscriptRef.current();

      // attempt to stop only if currently listening
      if (listeningRef.current) {
        stopListeningRef.current();
        recognitionRef.current?.abort();
      }

      activeDictationAttemptRef.current = null;
    };
  }, []);

  // temporarily use v2 eventing to quickly get STT usage insights
  const trackRetracked = useRetracked();
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;
  const handleScroll = React.useCallback<() => void>(() => {
    if (inputRef.current) {
      // auto-scroll text area so that overflow text is always visible
      inputRef.current.focus();
      inputRef.current.scrollTop = inputRef.current.scrollHeight;
    }
  }, []);

  const scrollDebounce = React.useMemo(() => {
    return debounce(handleScroll, 100);
  }, [handleScroll]);

  const trackDictationAttempt = React.useCallback<
    (
      status: 'completed' | 'cancelled' | 'failed',
      extra?: Pick<DictationAttemptTrackingData, 'cancelStage' | 'failureType'>
    ) => void
  >(
    (status, extra) => {
      const activeAttempt = activeDictationAttemptRef.current;

      if (!activeAttempt) {
        return;
      }

      if (dictationTrackingContext) {
        track('dictation_attempt', {
          ...dictationTrackingContext,
          attemptId: activeAttempt.attemptId,
          attemptStatus: status,
          ...extra,
        });
      }
      activeDictationAttemptRef.current = null;
    },
    [dictationTrackingContext, track]
  );

  const handleTranscript = React.useCallback<(transcript: string, isInterim: boolean) => void>(
    (transcript: string, isInterim: boolean) => {
      if (isInterim) {
        setInterimTranscript(truncateToRemainingLength(transcript, inputTextRef.current));
      } else {
        const activeAttempt = activeDictationAttemptRef.current;
        const appendedTranscript = truncateToRemainingLength(transcript, inputTextRef.current);
        const nextText = truncateToMaxLength(inputTextRef.current + transcript);

        if (activeAttempt && transcript.trim()) {
          activeAttempt.hasFinalTranscript = true;
        }

        if (appendedTranscript.trim()) {
          hasDictatedTextInDraftRef.current = true;
        }
        inputTextRef.current = nextText;
        setInputText(nextText);
        setInterimTranscript('');
      }

      scrollDebounce();
    },
    [scrollDebounce, truncateToMaxLength, truncateToRemainingLength]
  );

  const { isSupportedBrowser, isSupportedLanguage } = useSpeechToText({ onTranscript: handleTranscript });

  const handleClickOutside = React.useCallback<(event: MouseEvent) => void>((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsFocused(false);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  React.useEffect(() => {
    if (disableSpeechToText && listeningRef.current) {
      stopListeningRef.current();
    }
  }, [disableSpeechToText]);

  const sendButtonDisabled = disabled || (!inputText.trim() && !allowSendWithEmptyMessage);
  const speechToTextButtonDisabled = disabled || disableSpeechToText;

  const handleSend = () => {
    const messageToSend = inputText.trim();

    if (!messageToSend && !allowSendWithEmptyMessage) {
      return;
    }
    // Tracking needs to come before we call onSend in order to correctly track input for audio message
    trackRetracked({
      trackingData: {
        mode: trackingContext?.mode,
        input_text: inputText,
        // whether text was manually edited by user before sending
        was_text_edited: editedInput,
        // whether user used STT to create message
        is_listening: listeningRef.current,
      },
      trackingName: 'ai_coach_platform_richinputfield_send_message',
      action: 'click',
    });

    onSend?.(messageToSend, {
      inputMethod: getInputMethod(hasDictatedTextInDraftRef.current, editedInput),
    });

    setInputText('');
    inputTextRef.current = '';
    // reset edited input state for new message
    setEditedInput(false);
    hasDictatedTextInDraftRef.current = false;
    // put focus back on the input field after message is sent
    inputRef.current?.focus();
    // clear STT transcript after sending
    clearTranscriptRef.current();
    // if STT is active, stop listening after sending message
    if (listeningRef.current) {
      stopListeningRef.current();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      if (!sendButtonDisabled) {
        handleSend();
      }
    }
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (listeningRef.current) {
      // skip text input change when STT is active
      e.preventDefault();
      return;
    }

    const newText = truncateToMaxLength(e.target.value);

    inputTextRef.current = newText;
    setInputText(newText);
    onTextInputChange?.(newText);

    setEditedInput(true);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    // document.activeElement updates after the blur event, so we need to wait for the next tick
    setTimeout(() => {
      // maintain focus state if any of the children elements are focused
      if (!containerRef.current?.contains(document.activeElement as Node)) {
        setIsFocused(false);
      }
    }, 0);
  };

  const handleVoiceStart = () => {
    trackRetracked({
      trackingData: {
        mode: trackingContext?.mode,
      },
      trackingName: 'ai_coach_platform_stt_start',
      action: 'click',
    });
  };

  const handleVoiceEnd = () => {
    trackRetracked({
      trackingData: {
        // final transcribed text after voice input ends
        input_text: inputRef.current?.value,
        mode: trackingContext?.mode,
      },
      trackingName: 'ai_coach_platform_stt_stop',
      action: 'click',
    });
  };

  const handleVoiceStatusChange = React.useCallback<(status: SpeechRecognitionStatus) => void>(
    (status: SpeechRecognitionStatus) => {
      const activeAttempt = activeDictationAttemptRef.current;

      if (!activeAttempt) {
        return;
      }

      if (status === SpeechRecognitionStatus.SpeechStarted) {
        activeAttempt.hasSpeechDetected = true;
      }

      if (status === SpeechRecognitionStatus.StoppedListening) {
        if (activeAttempt.hasFinalTranscript) {
          trackDictationAttempt('completed');
          return;
        }

        trackDictationAttempt('cancelled', {
          cancelStage: activeAttempt.hasSpeechDetected
            ? 'after_speech_detected_before_final_transcript'
            : 'before_speech_detected',
        });
      }
    },
    [trackDictationAttempt]
  );

  const handleVoiceError = React.useCallback<(error: SpeechToTextError) => void>(
    (error) => {
      trackDictationAttempt('failed', {
        failureType: mapSpeechRecognitionErrorToFailureType(error),
      });
    },
    [trackDictationAttempt]
  );

  const handleVoiceClick = React.useCallback<(listening: boolean) => void>((listening: boolean) => {
    // Ignore restarts until the previous attempt reaches a terminal status so
    // we don't overwrite its attemptId before the final callback arrives.
    if (listening || activeDictationAttemptRef.current) {
      return;
    }

    const activeAttempt: ActiveDictationAttempt = {
      attemptId: randomUUID(),
      hasFinalTranscript: false,
      hasSpeechDetected: false,
    };

    activeDictationAttemptRef.current = activeAttempt;
  }, []);

  React.useImperativeHandle(forwardRef, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  const inputElementProps = {
    onKeyPress: handleKeyPress,
    required: false,
    className: 'coach-rich-input-field-input',
  };

  const sttEnabled = showSpeechToTextButton && isSupportedBrowser && isSupportedLanguage;

  return (
    <div
      className="coach-rich-input-field"
      role="group"
      aria-label={ariaLabel ?? _t('Chat message composer')}
      aria-disabled={disabled}
      css={styles.root({ isFocused, disabled })}
      ref={containerRef}
    >
      {input ?? (
        <div className="rich-input-field-content">
          <TextField
            readOnly={disabled || disableInput}
            placeholder={placeholder}
            autoFocus={disableInput ? false : autofocus}
            fullWidth
            multiline
            maxRows={7}
            minRows={1}
            maxLength={maxLength} // Enforces limit for typed/pasted input
            value={`${inputText}${interimTranscript}`}
            onChange={handleTextInputChange}
            inputProps={inputElementProps}
            aria-label={placeholder}
            data-testid="coach-rich-input-field-content"
            css={styles.textField(listeningRef.current)}
            inputRef={inputRef}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
      )}

      {preview ? (
        <div
          className="rich-input-field-preview"
          css={styles.preview}
          role="region"
          aria-label={_t('Media preview container')}
        >
          {preview}
        </div>
      ) : null}

      <div className="rich-input-field-footer" css={styles.footer} role="toolbar">
        {actionButtons ? (
          <div className="footer-action-buttons" css={styles.actionButtons}>
            {actionButtons}
          </div>
        ) : null}

        <div className="footer-voice-text-input" css={styles.voiceTextInput(disabled)}>
          {suggestedActions ? (
            <div className="footer-suggested-actions">
              <SuggestedActionsPopover
                actions={suggestedActions}
                disabled={disabled || inputText.length > 0 || listeningRef.current}
              />
            </div>
          ) : null}
          {sttEnabled ? (
            <SpeechToTextButton
              onTranscript={handleTranscript}
              onStartListening={handleVoiceStart}
              onStopListening={handleVoiceEnd}
              onStatusChange={handleVoiceStatusChange}
              onError={handleVoiceError}
              onClick={handleVoiceClick}
              disabled={speechToTextButtonDisabled}
              disableSpeechToText={disableSpeechToText}
              disabledTooltip={speechToTextDisabledTooltip}
            >
              {({ clearTranscript, listening, recognition, stopListening }) => {
                clearTranscriptRef.current = clearTranscript;
                recognitionRef.current = recognition;
                stopListeningRef.current = stopListening;
                listeningRef.current = listening;
                return null;
              }}
            </SpeechToTextButton>
          ) : null}

          <CoachIconButton
            onClick={handleSend}
            icon={<SendIcon color="default" />}
            aria-label={_t('Send message')}
            tooltip={_t('Send message')}
            disabled={sendButtonDisabled}
            size="small"
            onFocus={handleFocus}
            onBlur={handleBlur}
            data-testid="coach-send-message-button"
          />
        </div>
      </div>
    </div>
  );
});

export default RichInputField;
